import React, {useState} from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  IconButton,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useDeleteFolderMutation} from '../../../data/documentWorkspaceApi';
import PropTypes from 'prop-types';
import {DARK_TEXT_COLOR, WHITE_100} from '../../../theme';
import DeleteIcon from '../../../assets/file-delete.svg';

const styles = {
  modal: {
    base: {
      borderRadius: '0.5rem',
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
      backgroundColor: WHITE_100,
      minHeight: '220px',
    },
    title: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      color: '#162523',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '150%',
      letterSpacing: '-0.14px',
    },
    divider: {
      borderBottomWidth: '0.0625rem',
      opacity: 1,
      color: '#DDe0DE',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      lineHeight: '140%',
      color: DARK_TEXT_COLOR,
    },
  },
  createButton: {
    background: '#DC2626',
    borderRadius: '0.5rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    color: '#FFFFFF',
    fontSize: '0.875rem',
    fontWeight: '400',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#B91C1C',
    },
    '&:disabled': {
      backgroundColor: '#F87171',
      color: '#FFFFFF',
      opacity: 0.6,
    },
  },
  cancelButton: {
    borderRadius: '0.5rem',
    background: WHITE_100,
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    color: DARK_TEXT_COLOR,
    fontSize: '0.875rem',
    fontWeight: '400',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
};

const DeleteFolderModal = ({open, onClose, folder, onSuccess, onError}) => {
  const [confirmText, setConfirmText] = useState('');
  const [deleteFolder] = useDeleteFolderMutation();

  const handleSubmit = async () => {
    if (folder && confirmText) {
      try {
        const result = await deleteFolder({folderId: folder.id});

        if (result.error) {
          throw new Error(result.error);
        }

        setConfirmText('');
        onClose();
        if (onSuccess) {
          onSuccess(`Folder "${folder.name}" deleted successfully`);
        }
      } catch (error) {
        console.error('Error deleting folder:', error);
        if (onError) {
          onError('Failed to delete folder. Please try again.');
        }
      }
    }
  };

  const isEnabled = confirmText.toLowerCase() === 'confirm';

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        position={'absolute'}
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box width={'500px'} minWidth={'500px'} maxWidth={'500px'}>
          <Stack direction={'column'} sx={styles.modal.base}>
            <Stack
              direction={'row'}
              paddingY={'1rem'}
              paddingLeft={'1.5rem'}
              paddingRight={'1rem'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Typography sx={{...styles.modal.title, fontSize: '16px'}}>
                Confirm Deletion
              </Typography>
              <IconButton size={'small'} onClick={onClose}>
                <CloseIcon size={'1.25rem'} />
              </IconButton>
            </Stack>

            <Divider orientation={'horizontal'} sx={styles.modal.divider} />

            <Stack direction={'column'} padding={'1.5rem'}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={DeleteIcon} alt="Delete folder" />
              </Box>
              <Typography
                sx={styles.modal.label}
                marginBottom={'0.75rem'}
                marginTop={'1rem'}
              >
                Are you certain you want to proceed with the deletion of{' '}
                <strong>{folder.name}</strong>?{' '}
              </Typography>
              <Typography sx={styles.modal.label} marginBottom={'2.5rem'}>
                <strong>
                  This action will remove the folder along with all of its
                  contents.
                </strong>
              </Typography>
              <Typography
                sx={{...styles.modal.label, color: '#819190'}}
                marginBottom={'0.75rem'}
              >
                Type &quot;confirm&quot; to delete the folder
              </Typography>
              <TextField
                autoFocus
                fullWidth
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.5rem',
                    height: '2.5rem',
                    '&:hover fieldset': {
                      borderColor: '#26A993',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#26A993',
                    },
                  },
                }}
              />
            </Stack>
            <Divider orientation={'horizontal'} sx={styles.modal.divider} />
            <Stack
              direction={'row'}
              paddingY={'1rem'}
              paddingLeft={'1.5rem'}
              paddingRight={'1rem'}
              justifyContent={'flex-end'}
              alignItems={'center'}
              spacing={'0.625rem'}
            >
              <Button sx={styles.cancelButton} onClick={onClose}>
                Cancel
              </Button>
              <Button
                sx={styles.createButton}
                onClick={handleSubmit}
                disabled={!isEnabled}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default DeleteFolderModal;

DeleteFolderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  folder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    year: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};
